import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRsvpOpen } from '../../../utils/rsvpStatus';
import '../../../styles/AlexaFionaDebut.css';
import fallbackBgm from '../../../assets/sounds/Ed Sheeran - Perfect  Piano Cover with Strings (with PIANO SHEET).mp3';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const RSVP_DEADLINE = 'May 23, 2026';

const bills = [
  'Ramon Colapo',
  'Imelda Colapo',
  'Aj Soriano',
  'Alex Soriano',
  'Mary Jane Colapo',
  'Ramelyn Colapo',
  'Roxan Colapo',
  'Charlie Soriano',
  'Rose Ann Colapo',
];

const gifts = [
  'Tricia Samara Brides',
  'Prince Marwel Ladrera',
  'Bry Sabayo',
  'Janneth Albajeso',
  'Raissa Rebato',
  'Nicole Colapo',
  'Eugenio Katanyag',
  'Ashlee Rodolfu',
  'Joel Jerome Ho',
];

const wishes = [
  'Virginia Soriano',
  'Manilyn Ornada',
  'Jonhbie Ortega',
  'Johnvic Olithao',
  'Jaymark Feruelo',
  'Meldon Nahe',
  'Jei Delfin',
  'Keith Rabales',
  'Trish Singson',
  'Erwin Hernandez',
  'Khen Baculinao',
  'Angel Jazumi',
  'Jhaira Selda',
  'Raymond Colapo',
  'Nathaniel Colapo',
  'Grace Soriano',
  'Aiza Colapo',
  'Roxan Colapo',
];

export default function AlexaFionaDebut() {
  const [entered, setEntered] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError] = useState('');
  const [rsvpOpen, setRsvpOpen] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', phone: '', attendance: '', message: '' });
  const audioRef = useRef(null);

  useEffect(() => {
    fetchRsvpOpen('debut-alexa-fiona-colapo').then(setRsvpOpen);
  }, []);

  const startExperience = useCallback(() => {
    setEntered(true);
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => setPlaying(true)).catch(() => {});
  }, []);

  const toggleMusic = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    audio.play().then(() => setPlaying(true)).catch(() => {});
  }, [playing]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setRsvpError('');

    if (!form.name.trim()) {
      setRsvpError('Please enter your full name.');
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setRsvpError('Please enter a valid email address.');
      return;
    }
    if (!form.attendance) {
      setRsvpError('Please choose your attendance response.');
      return;
    }

    setRsvpLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitationId: 'debut-alexa-fiona-colapo',
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          guests: '1',
          attending: form.attendance,
          message: form.message.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'RSVP submission failed. Please try again.');
      }

      setRsvpSubmitted(true);
    } catch (error) {
      setRsvpError(error.message || 'Something went wrong while submitting RSVP.');
    } finally {
      setRsvpLoading(false);
    }
  };

  return (
    <div className="af-page">
      {!entered && (
        <div className="af-splash" onClick={startExperience}>
          <div className="af-splash-card">
            <p className="af-kicker">A Fairytale Debut Invitation</p>
            <h1>Alexa Fiona</h1>
            <p className="af-sub">June 23, 2026 | 7:00 PM</p>
            <button type="button" className="af-enter-btn" onClick={startExperience}>Enter Invitation</button>
          </div>
        </div>
      )}

      <button className={`af-music-btn${playing ? ' is-playing' : ''}`} onClick={toggleMusic} aria-label="Toggle background music">
        {playing ? 'Pause Music' : 'Play Music'}
      </button>

      <audio ref={audioRef} loop preload="auto">
        <source src={fallbackBgm} type="audio/mpeg" />
      </audio>

      <main className="af-main">
        <section className="af-hero">
          <p className="af-kicker">Debutant Name</p>
          <h2 className="af-name">Alexa Fiona</h2>
          <p className="af-event-title">An Evening of Elegance: The Debut of Alexa Fiona</p>
          <p className="af-details">June 23, 2026 | 7:00 PM</p>
          <p className="af-details">Jollibee Alimall GroundFloor</p>
        </section>

        <section className="af-card">
          <h3>Welcome Message</h3>
          <p>
            We are delighted to invite you to a night of elegance and celebration. Alexa Fiona warmly welcomes
            you to her eighteenth birthday. Let us share in an evening of laughter, beauty, and unforgettable
            moments. Your presence is the greatest gift of all.
          </p>
        </section>

        <section className="af-card af-two-col">
          <div>
            <h3>Parents</h3>
            <p>Roxan Colapo (father)</p>
            <p>Aiza Colapo (mother)</p>
          </div>
          <div>
            <h3>Attire</h3>
            <p>Any color is welcome.</p>
            <p>Color preference: lavender with pink.</p>
          </div>
        </section>

        <section className="af-card">
          <h3>18 Bills/Gifts</h3>
          <div className="af-grid-2">
            <div>
              <h4>Bills</h4>
              <ol>
                {bills.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ol>
            </div>
            <div>
              <h4>Gifts</h4>
              <ol start={10}>
                {gifts.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="af-card">
          <h3>18 Wishes</h3>
          <ol className="af-wishes">
            {wishes.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ol>
        </section>

        <section className="af-card">
          <h3>Gift Guide</h3>
          <p>
            Your presence is already a gift to us. But if you would like to give something, monetary gifts are
            appreciated, though thoughtful items are always welcome too.
          </p>
        </section>

        <section className="af-card">
          <h3>Reminder</h3>
          <p>
            A gentle reminder that the celebration will start at 7:00 PM sharp. We hope to see you there on time.
          </p>
        </section>

        <section className="af-card af-rsvp" id="rsvp">
          <h3>RSVP</h3>
          <p>Please confirm on or before {RSVP_DEADLINE}.</p>
          <p>RSVP Email: afscolapo@gmail.com</p>

          {!rsvpOpen ? (
            <div className="af-rsvp-closed">
              <p>🔒 RSVPs are now closed</p>
              <p>Thank you for your interest. The RSVP period for this event has ended.</p>
            </div>
          ) : !rsvpSubmitted ? (
            <form onSubmit={onSubmit} className="af-form">
              <label>
                Full Name
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>

              <label>
                Phone
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </label>

              <label>
                Attendance
                <select
                  value={form.attendance}
                  onChange={(e) => setForm((prev) => ({ ...prev, attendance: e.target.value }))}
                  required
                >
                  <option value="">Select an option</option>
                  <option value="yes">Yes, I will attend</option>
                  <option value="no">No, I cannot attend</option>
                </select>
              </label>

              <label>
                Message (optional)
                <textarea
                  rows="4"
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                />
              </label>

              {rsvpError && <p className="af-error">{rsvpError}</p>}
              <button type="submit" disabled={rsvpLoading}>
                {rsvpLoading ? 'Submitting...' : 'Send RSVP'}
              </button>
            </form>
          ) : (
            <p className="af-success">Thank you. Your RSVP has been received.</p>
          )}
        </section>
      </main>
    </div>
  );
}
